import { format, startOfMonth, startOfWeek } from 'date-fns';
import { ChartTooltipContainer } from '../ChartTooltipContainer';
import classNames from 'classnames';
import classes from './LineChartTooltip.module.scss';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';
import React from 'react';
import { SocialProfilesAnalytics } from '../../AnalyticsProvider';
import { STRING_DASHED_PROFILE, STRING_FAKE_VALUE_DASHED } from '../LineChart.view';
import { ChartLinePoint, ChartLine } from '../LineChart.controller';
import { useSettings } from 'hooks/useSettings';
import { StartOfWeekFormatSetting } from 'models/graphql';

type ChartLines = {
  dataKey?: string | number;
  name?: string | number;
  value?: (string | number)[] | string | number;
  payload?: {
    name: string;
    payload: ChartLine;
    date: Date;
    index: number;
  } & { [key: string]: ChartLinePoint };
}[];

type LineChartTooltipViewProps = {
  active?: boolean;
  payload?: ChartLines;
  socialProfiles: SocialProfilesAnalytics[];
  activeId: string | null;
  displayMonthly: boolean | undefined;
  isEngagementChart?: boolean;
  normalizedChart?: boolean;
  isWeekly: boolean | undefined;
  chartData: ChartLine[];
  hasPrefill?: boolean;
  hasPostFill?: boolean;
};

const getIdFromPayloadName = (payloadName: string) => {
  // Each payloadName represent a line in the chart.
  // Dashed lines will be "dashedLine-{profileId}.value"
  // Normal lines will be "{profileId}.normalizedValue"
  const pattern = /-(\d+)\./g; // REGEX to get a number with the - and . characters;
  const idWithDashAndDotCharacters = payloadName.match(pattern)?.[0];
  return idWithDashAndDotCharacters
    ? idWithDashAndDotCharacters.substring(1, idWithDashAndDotCharacters.length - 1)
    : payloadName.split('.fakeValue')[0]; // if idWithDashAndDotCharacters is undefined, is not a dashed line point
};

const getFormattedStringDateRange = (startRange: Date, endRange: Date, dateStringFormat: string) => {
  return `${format(startRange, dateStringFormat)} - ${format(endRange, dateStringFormat)}`;
};

export const LineChartTooltipView = (props: LineChartTooltipViewProps) => {
  const {
    chartData,
    active,
    payload,
    socialProfiles,
    activeId,
    displayMonthly,
    isEngagementChart,
    normalizedChart,
    isWeekly,
    hasPrefill,
    hasPostFill
  } = props;
  const { selectedOrgSettings } = useSettings();
  const startOfWeekSetting = selectedOrgSettings?.startOfWeekFormat ?? StartOfWeekFormatSetting.SUNDAY;
  const dayStartWeek = startOfWeekSetting === StartOfWeekFormatSetting.SUNDAY ? 0 : 1;

  if (active && payload && payload.length) {
    const date = payload[0].payload?.date ?? new Date();
    const indexChartPoint = payload[0].payload?.index ?? 0;
    const chartLines = payload.reduce((acc: ChartLines, currentValue) => {
      const payloadName = typeof currentValue.name === 'string' ? currentValue.name : '';
      if (payloadName.includes(STRING_FAKE_VALUE_DASHED)) {
        return [...acc];
      }
      const profileHasData = !payloadName.includes(STRING_DASHED_PROFILE);
      if (profileHasData) {
        return [...acc, currentValue];
      }
      const id = getIdFromPayloadName(payloadName);
      const idAlreadyExists = acc.some(
        chartLine => typeof chartLine.dataKey === 'string' && chartLine.dataKey.includes(id)
      );
      if (!idAlreadyExists) {
        return [...acc, currentValue];
      }
      return acc;
    }, []);

    const sortedByValue = chartLines.sort((data1, data2) => {
      if (typeof data2.value === 'number' && typeof data1.value === 'number') {
        return data2.value - data1.value;
      }
      return 0;
    });

    const weeklyFormat = 'EEE, MMM d, yyyy';
    const isTickFromPostFill = indexChartPoint === chartData.length - 2 && hasPostFill;
    const isTickFirstTickFromPrefill = (!isEngagementChart && indexChartPoint === 0) || indexChartPoint === 1;
    const dateFormatMonth =
      isTickFromPostFill || isTickFirstTickFromPrefill || isEngagementChart ? 'MMM d, yyyy' : 'MMM, yyyy';
    let tooltipTitle = displayMonthly ? format(date, dateFormatMonth) : format(date, weeklyFormat);

    if (isEngagementChart && (displayMonthly || isWeekly)) {
      if (indexChartPoint === 1 && hasPrefill) {
        const endDate = chartData[indexChartPoint + 1].date;
        tooltipTitle = getFormattedStringDateRange(date, endDate, isWeekly ? weeklyFormat : dateFormatMonth);
      } else if (indexChartPoint === chartData.length - 2 && hasPostFill) {
        const startRange = displayMonthly ? startOfMonth(date) : startOfWeek(date, { weekStartsOn: dayStartWeek });
        tooltipTitle = getFormattedStringDateRange(startRange, date, isWeekly ? weeklyFormat : dateFormatMonth);
      } else {
        const startRange = displayMonthly ? startOfMonth(date) : startOfWeek(date, { weekStartsOn: dayStartWeek });
        tooltipTitle = getFormattedStringDateRange(startRange, date, isWeekly ? weeklyFormat : dateFormatMonth);
      }
    }

    return (
      <ChartTooltipContainer>
        <h5>{tooltipTitle}</h5>
        <div>
          {sortedByValue.map(payloadObj => {
            if (payloadObj.payload) {
              const payloadName = typeof payloadObj.name === 'string' ? payloadObj.name : '';
              const profileHasData = !payloadName.includes(STRING_DASHED_PROFILE);
              const id = getIdFromPayloadName(payloadName);
              const socialProfile = socialProfiles.find(profile => profile.id === id);
              const formattedValue =
                payloadObj.value && typeof payloadObj.value === 'number' && payloadObj.value.toFixed(2);
              const percentageDifference = typeof formattedValue === 'string' ? parseFloat(formattedValue) : 0;
              let symbolToPercentageDiff = '';
              if (percentageDifference > 0) {
                symbolToPercentageDiff = '+';
              }

              const formattedRawValue = payloadObj.payload[id].rawValue?.toLocaleString('en-US');
              if (socialProfile) {
                return (
                  <div
                    className={classNames(classes.SocialProfileRow, { [classes.OnHoverProfile]: activeId === id })}
                    key={id}>
                    <SocialProfile
                      socialProfile={{
                        id: id,
                        provider: profilesHelpers.mapOAuthFromTypename(socialProfile.__typename),
                        network: profilesHelpers.mapNetworkFromTypename(socialProfile.__typename),
                        name: socialProfile.displayName
                      }}
                      size={'small'}
                      image={socialProfile.photoUrl}
                      label={true}
                    />
                    <div
                      className={classNames(classes.ValueContainer, {
                        [classes.ValueContainerPositive]:
                          normalizedChart && profileHasData && symbolToPercentageDiff === '+',
                        [classes.ValueContainerNegative]: normalizedChart && profileHasData && percentageDifference < 0
                      })}>
                      {normalizedChart ? (
                        <p>
                          {profileHasData
                            ? `${formattedRawValue} (${symbolToPercentageDiff}${
                                percentageDifference === 0 ? '+0.00' : percentageDifference.toFixed(2)
                              }%)`
                            : 'No Data'}
                        </p>
                      ) : (
                        <p> {profileHasData ? `${formattedRawValue}` : 'No Data'}</p>
                      )}
                    </div>
                  </div>
                );
              } else {
                return <></>;
              }
            }
            return <></>;
          })}
        </div>
      </ChartTooltipContainer>
    );
  }
  return null;
};
